import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { Rocket, AlertCircle, Cpu } from 'lucide-react';

interface DiscoveryPlan {
    plan_content: string;
    url: string;
    framework: string;
    created_at: string;
}

interface DiscoveryTabProps {
    entityId: string;
    projectId: string;
}

const DiscoveryTab: React.FC<DiscoveryTabProps> = ({ entityId, projectId }) => {
    const [plan, setPlan] = useState<DiscoveryPlan | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPlan = async () => {
            try {
                const data = await api.getDiscoveryPlan(projectId, entityId);
                if (data.plan_content) {
                    setPlan(data);
                }
            } catch (err) {
                console.error("Error loading discovery plan", err);
            } finally {
                setLoading(false);
            }
        };
        fetchPlan();
    }, [entityId, projectId]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!plan) {
        return (
            <div className="text-center py-12 border border-dashed border-slate-700 rounded-lg">
                <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Rocket className="w-6 h-6 text-slate-500" />
                </div>
                <h3 className="text-slate-300 font-medium mb-2">No hay inteligencia de descubrimiento aún</h3>
                <p className="text-slate-500 text-sm max-w-sm mx-auto">
                    Ejecuta <code>flowspecqa discover</code> para esta entidad para poblar la inteligencia de la UI.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header / Meta */}
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
                <div className="flex items-center gap-3">
                    <div className="bg-purple-500/10 p-2 rounded-lg">
                        <Cpu className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                        <h3 className="text-lg font-medium text-white">Discovery Plan Intelligence</h3>
                        <p className="text-xs text-slate-400">Capturado desde: <span className="text-blue-400 font-mono italic">{plan.url}</span></p>
                    </div>
                </div>
                <div className="text-right">
                    <span className="text-[10px] text-slate-500 uppercase block tracking-wider">Último Escaneo</span>
                    <span className="text-xs text-slate-300 font-medium">{new Date(plan.created_at).toLocaleString()}</span>
                </div>
            </div>

            {/* Plan Content Viewer */}
            <div className="bg-slate-950/50 rounded-xl border border-slate-800 p-6 shadow-inner">
                <div className="prose prose-invert max-w-none">
                    <pre className="whitespace-pre-wrap font-sans text-sm text-slate-300 leading-relaxed">
                        {plan.plan_content}
                    </pre>
                </div>
            </div>

            {/* AI Warning / Context */}
            <div className="bg-blue-500/10 p-4 rounded-lg border border-blue-500/20 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5" />
                <div>
                   <h4 className="text-sm font-semibold text-blue-300">AI Context Aware</h4>
                   <p className="text-xs text-blue-400/80 leading-relaxed">
                       Este plan es utilizado por el Generador de Automatización para construir selectores y flujos de alta precisión. 
                       Contiene un escaneo estructural de los elementos interactivos detectados.
                   </p>
                </div>
            </div>
        </div>
    );
};

export default DiscoveryTab;
